import { v4 as uuidv4 } from 'uuid';
import { defaultPdfAnchor } from './signatureFieldCoordinates';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export function formatCpfMask(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function cpfDigitsOnly(cpf) {
  const d = String(cpf || '').replace(/\D/g, '');
  return d || undefined;
}

export function formatTelefoneMask(raw) {
  let digits = String(raw || '').replace(/\D/g, '').slice(0, 13);
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  digits = digits.slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function telefoneDigitsOnly(telefone) {
  let d = String(telefone || '').replace(/\D/g, '');
  if (!d) return undefined;
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) {
    d = d.slice(2);
  }
  return d || undefined;
}

export function isValidTelefone(telefone) {
  const d = telefoneDigitsOnly(telefone);
  if (!d || (d.length !== 10 && d.length !== 11)) return false;
  const ddd = Number(d.slice(0, 2));
  return ddd >= 11 && ddd <= 99;
}

export function isValidEmail(email) {
  return EMAIL_RE.test(normalizeEmail(email));
}

export const NOTIFICATION_CHANNELS = Object.freeze({
  EMAIL: 'Email',
  WHATSAPP: 'Whatsapp',
});

export function normalizeNotificationChannel(raw) {
  const value = String(raw || '').trim().toLowerCase();
  if (!value || value === 'email') return NOTIFICATION_CHANNELS.EMAIL;
  if (value === 'whatsapp') return NOTIFICATION_CHANNELS.WHATSAPP;
  if (raw === NOTIFICATION_CHANNELS.EMAIL || raw === NOTIFICATION_CHANNELS.WHATSAPP) {
    return raw;
  }
  return null;
}

/**
 * @returns {import('./signatureFieldModel').SignatureField}
 */
export function createSignatureField(pageNumber, pdfPageWidthPt, pdfPageHeightPt) {
  const signerId = uuidv4();
  const anchor = defaultPdfAnchor(pageNumber, signerId, pdfPageWidthPt, pdfPageHeightPt);

  return {
    signer_id: signerId,
    full_name: '',
    email: '',
    cpf: '',
    telefone: '',
    page_number: anchor.page_number,
    x: anchor.x,
    y: anchor.y,
    width: anchor.width,
    height: anchor.height,
    page_relative: true,
    pdf_page_width_pt: anchor.pdf_page_width_pt,
    pdf_page_height_pt: anchor.pdf_page_height_pt,
    isDraft: true,
    isConfirmed: false,
  };
}

/**
 * Merge API manual_signers + signature_anchors by signer_id.
 * @param {Array<object>|null|undefined} manualSigners
 * @param {Array<object>|null|undefined} signatureAnchors
 */
export function mergeSignersAndAnchors(manualSigners, signatureAnchors) {
  const signers = Array.isArray(manualSigners) ? manualSigners : [];
  const anchors = Array.isArray(signatureAnchors) ? signatureAnchors : [];
  const anchorById = Object.fromEntries(
    anchors.filter((a) => a?.signer_id).map((a) => [a.signer_id, a])
  );
  const signerIds = new Set([
    ...signers.map((s) => s.signer_id).filter(Boolean),
    ...anchors.map((a) => a.signer_id).filter(Boolean),
  ]);

  if (signerIds.size === 0 && signers.length > 0) {
    return signers.map((s, idx) => {
      const anchor = anchors[idx];
      const signerId = s.signer_id || anchor?.signer_id || uuidv4();
      return buildFieldFromParts(signerId, s, anchor);
    });
  }

  return [...signerIds].map((signerId) => {
    const signer = signers.find((s) => s.signer_id === signerId) || {};
    const anchor = anchorById[signerId];
    return buildFieldFromParts(signerId, signer, anchor);
  });
}

function buildFieldFromParts(signerId, signer, anchor) {
  const fullName = String(signer.full_name || signer.nome || '').trim();
  const email = String(signer.email || '').trim();
  const cpf = signer.cpf ? formatCpfMask(signer.cpf) : '';
  const telefoneRaw = signer.telefone || signer.whatsapp_phone_number || '';
  const telefone = telefoneRaw ? formatTelefoneMask(telefoneRaw) : '';

  if (!anchor) {
    return {
      signer_id: signerId,
      full_name: fullName,
      email,
      cpf,
      telefone,
      page_number: 1,
      x: 72,
      y: 640,
      width: 220,
      height: 40,
      page_relative: true,
      pdf_page_width_pt: 595,
      pdf_page_height_pt: 842,
      isDraft: !fullName,
      isConfirmed: Boolean(fullName),
    };
  }

  return {
    signer_id: signerId,
    full_name: fullName,
    email,
    cpf,
    telefone,
    page_number: anchor.page_number,
    x: anchor.x,
    y: anchor.y,
    width: anchor.width,
    height: anchor.height,
    page_relative: true,
    pdf_page_width_pt: anchor.pdf_page_width_pt,
    pdf_page_height_pt: anchor.pdf_page_height_pt,
    isDraft: !fullName,
    isConfirmed: Boolean(fullName),
  };
}

/**
 * @param {Array<object>} fields
 */
export function splitFieldsToSavePayload(fields) {
  const confirmed = (fields || []).filter((f) => f.isConfirmed);

  return {
    manual_signers: confirmed.map((f) => {
      const signer = {
        signer_id: f.signer_id,
        full_name: String(f.full_name || '').trim(),
      };
      const email = normalizeEmail(f.email);
      if (email) signer.email = email;
      const cpf = cpfDigitsOnly(f.cpf);
      if (cpf) signer.cpf = cpf;
      const telefone = telefoneDigitsOnly(f.telefone);
      if (telefone) signer.telefone = telefone;
      return signer;
    }),
    signature_anchors: confirmed.map((f) => ({
      signer_id: f.signer_id,
      page_number: f.page_number,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
      page_relative: true,
      pdf_page_width_pt: f.pdf_page_width_pt,
      pdf_page_height_pt: f.pdf_page_height_pt,
    })),
  };
}

export const MISSING_SIGNATURE_FIELDS_MESSAGE =
  'Posicione os campos de assinatura no PDF antes de enviar.';

/**
 * @param {Array<object>} fields
 * @param {{ forSend?: boolean, requireEmail?: boolean, requireTelefone?: boolean }} [options]
 * @returns {{ ok: true } | { ok: false; message: string }}
 */
export function validateSignatureFields(
  fields,
  { forSend = false, requireEmail = false, requireTelefone = false } = {}
) {
  const confirmed = (fields || []).filter((f) => f.isConfirmed);

  const draft = (fields || []).find((f) => f.isDraft && !f.isConfirmed);
  if (draft) {
    return { ok: false, message: 'Confirme ou remova o campo de assinatura em edição.' };
  }

  if (confirmed.length === 0) {
    if (forSend) {
      return { ok: false, message: MISSING_SIGNATURE_FIELDS_MESSAGE };
    }
    return { ok: true };
  }

  const emails = new Set();

  for (const field of confirmed) {
    const name = String(field.full_name || '').trim();
    if (!name) {
      return { ok: false, message: 'Nome completo é obrigatório em todos os campos.' };
    }

    const emailRaw = String(field.email || '').trim();
    if (emailRaw) {
      if (!isValidEmail(field.email)) {
        return { ok: false, message: `E-mail inválido para "${name}".` };
      }
      const normalized = normalizeEmail(field.email);
      if (emails.has(normalized)) {
        return { ok: false, message: 'Cada campo deve ter um e-mail único.' };
      }
      emails.add(normalized);
    } else if (requireEmail) {
      return {
        ok: false,
        message: 'Para envio via E-mail, todos os signatários devem ter e-mail.',
      };
    }

    const cpf = cpfDigitsOnly(field.cpf);
    if (cpf && cpf.length !== 11) {
      return { ok: false, message: `CPF inválido para "${name}" (informe 11 dígitos).` };
    }

    const telefoneDigits = telefoneDigitsOnly(field.telefone);
    if (telefoneDigits && !isValidTelefone(field.telefone)) {
      return {
        ok: false,
        message: `Telefone inválido para "${name}" (informe DDD + número).`,
      };
    }
    if (requireTelefone && !isValidTelefone(field.telefone)) {
      return {
        ok: false,
        message:
          'Para envio via WhatsApp, todos os signatários devem ter telefone com DDD.',
      };
    }

    if (
      field.width <= 0 ||
      field.height <= 0 ||
      field.x < 0 ||
      field.y < 0 ||
      field.x + field.width > field.pdf_page_width_pt + 0.01 ||
      field.y + field.height > field.pdf_page_height_pt + 0.01
    ) {
      return { ok: false, message: `Geometria inválida para "${name}".` };
    }
  }

  return { ok: true };
}

export function hasUnsavedDraft(fields) {
  return (fields || []).some((f) => f.isDraft && !f.isConfirmed);
}
